import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = ['status', 'project', 'hidden', 'modal', 'input', 'hint'];

  connect() {
    this.lifecycleStatusIds = (JSON.parse(this.data.get('statuses') || '[]')).map(String);
    this.originalStatus = this.data.get('original-status') || '';
    this.isEdit = this.data.get('edit') === 'true';
    this.appendProject = this.data.get('append-project') === 'true';
    this.previousStatus = this.statusTarget?.value || '';
    this.revertStatus = this.previousStatus || this.originalStatus || '';
    this.confirmedStatus = '';

    this.form = this.element.closest('form');
    this.boundGuardSubmit = (e) => this.guardSubmit(e);
    this.form?.addEventListener('submit', this.boundGuardSubmit);
    this.refresh();
  }

  disconnect() {
    this.form?.removeEventListener('submit', this.boundGuardSubmit);
    document.body.style.overflow = '';
  }

  refresh() {
    const statusId = this.statusTarget?.value || '';

    if (this.lifecycleRequired(statusId)) {
      if (String(statusId) !== String(this.confirmedStatus)) {
        this.lifecycleProject = '';
        this.revertStatus = this.previousStatus || this.originalStatus || '';
      }
      if (!this.lifecycleProject) this.showModal();
    } else {
      this.lifecycleProject = '';
      this.confirmedStatus = '';
      this.previousStatus = statusId;
    }

    this.updateHint();
  }

  onProjectInput() {
    if (this.appendProject && this.lifecycleProject && this.hasProjectTarget) {
      const projects = this.projectTarget.value.split(',').map((p) => p.trim());
      if (!projects.includes(this.lifecycleProject)) {
        this.lifecycleProject = '';
        this.confirmedStatus = '';
        if (this.hasStatusTarget) {
          this.statusTarget.value = this.revertStatus ?? this.originalStatus ?? '';
          this.previousStatus = this.statusTarget.value;
        }
      }
    }
    this.updateHint();
  }

  get lifecycleProject() {
    return this.hiddenTarget?.value.trim() || '';
  }

  set lifecycleProject(value) {
    if (this.hasHiddenTarget) this.hiddenTarget.value = value;
  }

  updateHint() {
    if (!this.hasHintTarget) return;

    const active = this.lifecycleRequired(this.statusTarget?.value);
    const project = this.lifecycleProject;

    this.hintTarget.classList.toggle('-state-hidden', !active);
    this.hintTarget.classList.toggle('lc-lifecycle-hint-warning', active && !project);
    this.hintTarget.textContent = !active ? ''
      : project ? `Lifecycle event will be recorded against project: ${project}`
      : 'A project ID is required for this lifecycle event.';
  }

  lifecycleRequired(statusId) {
    if (!statusId) return false;
    return this.lifecycleStatusIds.includes(String(statusId))
      && (!this.isEdit || String(statusId) !== String(this.originalStatus));
  }

  showModal() {
    if (!this.hasModalTarget) return;
    if (this.hasInputTarget) this.inputTarget.value = this.lifecycleProject;
    this.modalTarget.classList.remove('-state-hidden');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => this.inputTarget?.focus());
  }

  hideModal() {
    if (!this.hasModalTarget) return;
    this.modalTarget.classList.add('-state-hidden');
    document.body.style.overflow = '';
  }

  confirmModal() {
    const value = this.inputTarget?.value.trim();
    if (!value) {
      this.inputTarget?.focus();
      return;
    }

    this.lifecycleProject = value;

    if (this.appendProject && this.hasProjectTarget) {
      const projects = this.projectTarget.value.split(',').map((p) => p.trim()).filter(Boolean);
      if (!projects.includes(value)) this.projectTarget.value = [...projects, value].join(', ');
    }

    this.confirmedStatus = this.statusTarget.value;
    this.previousStatus = this.statusTarget.value;
    this.hideModal();
    this.updateHint();
  }

  cancelModal() {
    if (this.hasStatusTarget) {
      this.statusTarget.value = this.previousStatus || this.originalStatus || '';
    }
    this.confirmedStatus = '';
    this.lifecycleProject = '';
    this.hideModal();
    this.updateHint();
  }

  inputKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmModal();
    }
  }

  guardSubmit(event) {
    if (!this.hasHiddenTarget || !this.lifecycleRequired(this.statusTarget?.value)) return;
    if (this.lifecycleProject) return;
    event.preventDefault();
    this.showModal();
  }
}
